<script lang="ts">
	import { Hr, Popover } from "flowbite-svelte";
	import { A, ImagePlaceholder } from "flowbite-svelte-v1";
	import { SvelteSet } from "svelte/reactivity";
	import Icon from "#lib/components/Icon.svelte";
	import FileManagerDialog from "#lib/components/images/file-management/FileManagerDialog.svelte";
	import type { ImagePickerChangeEvent } from "#lib/components/images/types.js";
	import { ICONS } from "#lib/icons.js";
	import Button from "#lib/ui/Button/index.js";

	interface Props {
		id?: string;
		iconSet?: string;
		name?: string;
		onChange?: (iconSet: string | undefined, name: string | undefined) => void;
		allowImageSelection?: boolean;
		onImageChange?: (value: string) => void;
	}

	let { id, iconSet, name, onChange, allowImageSelection = false, onImageChange }: Props = $props();

	const uniqueIcons = $derived(Array.from(new SvelteSet(ICONS)).sort());
	let open = $state(false);
	let fileManagerOpen = $state(false);

	function isSelected(ticonSet: string | undefined, tname: string | undefined): boolean {
		return iconSet === ticonSet && name === tname;
	}

	function handleIconSelected(event: MouseEvent, ticonSet: string, tname: string): void {
		event.preventDefault();
		event.stopPropagation();
		iconSet = ticonSet;
		name = tname;
		onChange?.(iconSet, name);
		open = false;
	}

	function handleClearSelection(event: MouseEvent): void {
		event.preventDefault();
		event.stopPropagation();
		iconSet = undefined;
		name = undefined;
		onChange?.(iconSet, name);
		open = false;
	}

	function handleUploadMyOwn(event: MouseEvent): void {
		event.preventDefault();
		event.stopPropagation();
		open = false;
		fileManagerOpen = true;
	}

	function handleImageSelected(e: CustomEvent<ImagePickerChangeEvent>): void {
		const value = e.detail.value;
		if (value) {
			onImageChange?.(value);
		}
		fileManagerOpen = false;
	}
</script>

{#if fileManagerOpen}
	<FileManagerDialog bind:open={fileManagerOpen} aspectRatio={1} imageTypeCaption="icon" on:change={handleImageSelected} />
{/if}

<div class="flex-none">
	<Button data-action-name="icon-picker-toggle" color="light" size="sm" class="group aspect-square rounded-lg border-gray-300 bg-gray-50 p-2 hover:bg-gray-100" {id}>
		{#if iconSet && name}
			<Icon {iconSet} {name} class="h-6 w-6" />
		{:else}
			<ImagePlaceholder size="sm" imgOnly={true} class="h-6 w-6 animate-none" classes={{ image: "h-full sm:w-full w-full bg-gray-50 group-hover:bg-gray-100 m-0", svg: "text-gray-400 h-4 w-4" }} />
		{/if}
	</Button>
	<Popover class="z-10" placement="bottom-start" trigger="click" {open} on:show={(e: CustomEvent) => (open = e.detail)}>
		<div class="grid max-h-54 grid-cols-6 overflow-x-hidden overflow-y-auto">
			{#each uniqueIcons as icon (icon)}
				{@const [iconSet, name] = icon.split(":")}
				<div class="flex items-center justify-center">
					<button type="button" data-action-name="icon-picker-icon" data-action-title={name} onclick={e => handleIconSelected(e, iconSet, name)} class="group/item aspect-square rounded-xl p-2 text-base text-gray-600" class:hover:bg-gray-100={isSelected(iconSet, name)} class:hover:text-gray-900={isSelected(iconSet, name)} class:bg-gray-200={isSelected(iconSet, name)}>
						<Icon {iconSet} {name} class="m-auto h-6 w-6 transition ease-in-out group-hover/item:scale-125" />
					</button>
				</div>
			{/each}
		</div>
		<Hr hrClass="my-1.5" />
		<div class="flex items-center gap-2 py-1" class:justify-end={!allowImageSelection} class:justify-between={allowImageSelection} class:w-full={allowImageSelection}>
			{#if allowImageSelection}
				<A data-action-name="icon-picker-upload-my-own" onclick={handleUploadMyOwn}>Upload image instead</A>
			{/if}
			<Button data-action-name="icon-picker-clear" color="light" size="xs" on:click={handleClearSelection} disabled={!iconSet && !name}>Clear</Button>
		</div>
	</Popover>
</div>
